import { logoPerformanceMonitor } from '../utils/logoPerformance';

interface LogoCache {
  [key: string]: HTMLImageElement;
}

class LogoPreloader {
  private logoCache: LogoCache = {};
  private loadingPromises: Promise<void>[] = [];
  private isInitialized = false;

  private readonly logoUrls = [
    '/images/lts.svg',
    '/images/logo_livetrading_studio_1.svg',
    '/images/logo_symbol.svg',
    '/images/logo_livetrading_studio.svg'
  ];

  // Priority order for loading (most important first)
  private readonly priorityOrder = [
    '/images/lts.svg',
    '/images/logo_symbol.svg',
    '/images/logo_livetrading_studio_1.svg',
    '/images/logo_livetrading_studio.svg'
  ];

  /**
   * Preload all logo images and cache them for instant access
   */
  async preloadAllLogos(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Start performance monitoring
    logoPerformanceMonitor.startMonitoring();
    
    const startTime = performance.now();
    
    try {
      // Load logos in priority order, but allow parallel loading within each priority group
      const priorityGroups = [
        [this.priorityOrder[0]], // Most critical logo first
        this.priorityOrder.slice(1) // Rest in parallel
      ];
      
      for (const group of priorityGroups) {
        const groupPromises = group.map(url => this.preloadLogo(url));
        await Promise.allSettled(groupPromises); // Use allSettled to not fail on individual errors
      }
      
      const loadTime = performance.now() - startTime;
      
      // Log performance summary
      logoPerformanceMonitor.logPerformanceSummary();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Error preloading logos:', error);
      // Continue even if some logos fail to load
      this.isInitialized = true;
    }
  }

  /**
   * Preload a single logo image with timeout and retry logic
   */
  private preloadLogo(url: string, retryCount = 0): Promise<void> {
    return new Promise((resolve, reject) => {
      // Record start time for performance monitoring
      logoPerformanceMonitor.recordLogoStart(url);
      
      const img = new Image();
      let timeoutId: NodeJS.Timeout;
      
      // Set a timeout to prevent hanging (3 seconds)
      timeoutId = setTimeout(() => {
        if (retryCount < 1) {
          // Retry once
          this.preloadLogo(url, retryCount + 1)
            .then(resolve)
            .catch(reject);
        } else {
          logoPerformanceMonitor.recordLogoComplete(url, 0);
          resolve(); // Resolve anyway to not block other logos
        }
      }, 3000);
      
      img.onload = () => {
        clearTimeout(timeoutId);
        this.logoCache[url] = img;
        
        // Record completion time and size for performance monitoring
        logoPerformanceMonitor.recordLogoComplete(url, img.naturalWidth * img.naturalHeight);
        
        resolve();
      };
      
      img.onerror = () => {
        clearTimeout(timeoutId);
        if (retryCount < 1) {
          // Retry on error
          this.preloadLogo(url, retryCount + 1)
            .then(resolve)
            .catch(reject);
        } else {
          // Record completion even on error for monitoring
          logoPerformanceMonitor.recordLogoComplete(url, 0);
          resolve(); // Resolve anyway to not block other logos
        }
      };
      
      // Load the image (no crossOrigin needed for local images)
      img.src = url;
    });
  }

  /**
   * Get a preloaded logo image element
   */
  getLogo(url: string): HTMLImageElement | null {
    return this.logoCache[url] || null;
  }

  /**
   * Check if all logos are loaded
   */
  isReady(): boolean {
    return this.isInitialized && this.loadingPromises.length > 0;
  }

  /**
   * Get loading progress (0-1)
   */
  getLoadingProgress(): number {
    if (this.loadingPromises.length === 0) return 1;
    
    const loadedCount = Object.keys(this.logoCache).length;
    return loadedCount / this.logoUrls.length;
  }

  /**
   * Get performance metrics for a specific logo
   */
  getLogoPerformance(url: string): { loadTime: number; size: number } | null {
    // This would need to be implemented in the performance monitor
    // For now, return null
    return null;
  }

  /**
   * Clear cache (useful for testing or memory management)
   */
  clearCache(): void {
    this.logoCache = {};
    this.isInitialized = false;
    this.loadingPromises = [];
    logoPerformanceMonitor.clearMetrics();
  }
}

// Export singleton instance
export const logoPreloader = new LogoPreloader();
export default LogoPreloader;
